import React, { useState } from 'react';
import { Box, Typography, IconButton, FormControlLabel, Checkbox, TextField, Button, Popover, List, ListItem, ListItemText } from '@mui/material';
import { AspectRatioRoundedIcon, CheckIcon, FileDownloadIcon } from 'icons/materialIcons';

const TeamEventBadgesLeft = ({ selectedOption, onSelectionChange, checkedItems, onDownload, onPrint, onCheckboxChange, callToActionText, onCallToActionChange }) => {
    const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
    const downloadMenuOpen = Boolean(downloadAnchorEl);
    const downloadMenuId = downloadMenuOpen ? 'download-popover' : undefined;

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;

        if (name === 'qrCode') {
            // When QR code is being turned on, automatically turn on the logo too
            if (checked) {
                onCheckboxChange({
                    target: { name: 'qrCode', checked: true }
                });
                onCheckboxChange({
                    target: { name: 'qrCodeLogo', checked: true }
                });
            } else {
                // When QR code is being turned off, turn off both logo and call to action
                onCheckboxChange({
                    target: { name: 'qrCode', checked: false }
                });
                onCheckboxChange({
                    target: { name: 'qrCodeLogo', checked: false }
                });
                onCheckboxChange({
                    target: { name: 'callToAction', checked: false }
                });
            }
        } else if (name === 'callToAction') {
            if (checkedItems.qrCode) {
                onCheckboxChange(event);
            }
        } else {
            onCheckboxChange(event);
        }
    };

    const handleCallToActionTextChange = (event) => {
        onCallToActionChange(event.target.value);
    };

    const handleDownloadButtonClick = (event) => {
        setDownloadAnchorEl(event.currentTarget);
    };

    const handleDownloadMenuClose = () => {
        setDownloadAnchorEl(null);
    };

    const handleDownloadOption = (format) => {
        onDownload(format);
        handleDownloadMenuClose();
    };

    const handlePrintButtonClick = () => {
        onPrint();
    };

    return (
        <Box sx={{ flex: 1, padding: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 150px)',
                    '&::-webkit-scrollbar': {
                        width: '0px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                }}
            >
                <Typography variant="h5" sx={{ mb: 2, ml: '5px' }}>
                    Event Badges
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        mb: 3,
                        ml: '5px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#4F4F4F',
                        letterSpacing: '0.05em',
                    }}
                >
                    Download a printable QR code event badge for any upcoming events.
                    The QR code will automatically share the member's Touch digital business card with others.
                </Typography>

                {/* Badge Type Selection */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    {['badge', 'nametag', 'square'].map((option) => (
                        <Box
                            key={option}
                            onClick={() => onSelectionChange(option)}
                            sx={{
                                textAlign: 'center',
                                backgroundColor: '#f5f5f5',
                                color: '#000',
                                padding: 2,
                                borderRadius: '10px',
                                flex: 1,
                                mx: 2,
                                cursor: 'pointer',
                                border: `2px solid ${selectedOption === option ? '#2aaff7' : 'transparent'}`,
                                transition: 'border 0.2s ease-in-out',
                            }}
                        >
                            <IconButton sx={{ color: selectedOption === option ? '#000' : 'inherit', mb: 1 }}>
                                <AspectRatioRoundedIcon />
                            </IconButton>
                            <Typography sx={{ mb: 1 }}>
                                {option.charAt(0).toUpperCase() + option.slice(1)}
                            </Typography>

                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    backgroundColor: selectedOption === option ? '#2aaff7' : '#ffffff',
                                    border: `2px solid ${selectedOption === option ? '#2aaff7' : '#ccc'}`,
                                    margin: 'auto',
                                    position: 'relative',
                                }}
                            >
                                {selectedOption === option && (
                                    <CheckIcon
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            color: '#ffffff',
                                            fontSize: 18,
                                            fontWeight: 700,
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Checkbox Options */}
                <Box sx={{ mt: 3, backgroundColor: '#f5f5f5', padding: '10px 15px', borderRadius: '10px' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
                        {['profilePhoto', 'name', 'jobTitle', 'company', 'location', 'qrCode', 'callToAction'
                            /* 'qrCodeLogo' removed from the list */
                        ].map((item) => (
                            <FormControlLabel
                                key={item}
                                control={
                                    <Checkbox
                                        checked={checkedItems[item]}
                                        onChange={handleCheckboxChange}
                                        name={item}
                                        color="primary"
                                        disabled={
                                            (item === 'callToAction') &&
                                            !checkedItems.qrCode
                                        }
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: '14px' }}>
                                        {item.charAt(0).toUpperCase() + item.slice(1).replace(/([A-Z])/g, ' $1')}
                                    </Typography>
                                }
                            />
                        ))}
                    </Box>
                </Box>

                {/* Call to Action Text Input */}
                <Box sx={{ mt: 3, borderRadius: '10px' }}>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 400, fontSize: '14px' }}>
                        Call to Action Text
                    </Typography>
                    <TextField
                        variant="outlined"
                        fullWidth
                        value={callToActionText}
                        onChange={handleCallToActionTextChange}
                        disabled={!checkedItems.callToAction}
                        sx={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: '10px',
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: 'none',
                                },
                                '&:hover fieldset': {
                                    border: 'none',
                                },
                                '&.Mui-focused fieldset': {
                                    border: 'none',
                                },
                                fontSize: '14px',
                            },
                        }}
                        placeholder="Call to action text"
                    />
                </Box>
            </Box>

            {/* Buttons Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                    variant="outlined"
                    sx={{
                        borderRadius: '20px',
                        borderColor: '#dedede',
                        color: '#000',
                        padding: '4px 45px',
                        flex: 1,
                        marginRight: '8px',
                        textTransform: 'none',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleDownloadButtonClick}
                    aria-describedby={downloadMenuId}
                    sx={{
                        borderRadius: '20px',
                        backgroundColor: '#000',
                        color: '#fff',
                        padding: '4px 45px',
                        flex: 1,
                        marginRight: '8px',
                        textTransform: 'none',
                    }}
                >
                    Download
                </Button>
                <Popover
                    id={downloadMenuId}
                    open={downloadMenuOpen}
                    anchorEl={downloadAnchorEl}
                    onClose={handleDownloadMenuClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    sx={{
                        '& .MuiPaper-root': {
                            borderRadius: '10px',
                            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                        },
                    }}
                >
                    <Typography
                        sx={{
                            px: 2,
                            pt: 1.5,
                            fontWeight: 600,
                            color: '#4F4F4F'
                        }}
                    >
                        Download
                    </Typography>
                    <List sx={{ pt: 0, pb: 0.5 }}>
                        <ListItem
                            button
                            onClick={() => handleDownloadOption('png')}
                            sx={{
                                py: 0.5,
                                cursor: 'pointer',  // Explicitly set the cursor to pointer
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                }
                            }}
                        >
                            <FileDownloadIcon sx={{ mr: 1, fontSize: 20, color: '#4F4F4F' }} />
                            <ListItemText primary="PNG" />
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => handleDownloadOption('pdf')}
                            sx={{
                                py: 0.5,
                                cursor: 'pointer',  // Explicitly set the cursor to pointer
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                }
                            }}
                        >
                            <FileDownloadIcon sx={{ mr: 1, fontSize: 20, color: '#4F4F4F' }} />
                            <ListItemText primary="PDF" />
                        </ListItem>
                    </List>
                </Popover>
                <Button
                    variant="contained"
                    onClick={handlePrintButtonClick}
                    sx={{
                        borderRadius: '20px',
                        backgroundColor: '#000',
                        color: '#fff',
                        padding: '4px 45px',
                        textTransform: 'none',
                        flex: 1,
                    }}
                >
                    Print
                </Button>
            </Box>
        </Box>
    );
};

export default TeamEventBadgesLeft;